import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { SubscriptionProvider, useSubscription } from "./lib/subscription";
import Layout from "./components/Layout";

import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import AddInvoicePage from "./pages/AddInvoicePage";
import ReviewInvoicePage from "./pages/ReviewInvoicePage";
import InvoiceRegisterPage from "./pages/InvoiceRegisterPage";
import ReportsPage from "./pages/ReportsPage";
import MonthlyHistoryPage from "./pages/MonthlyHistoryPage";
import SettingsPage from "./pages/SettingsPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import TermsPage from "./pages/TermsPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import AdminCouponsPage from "./pages/AdminCouponsPage";

import "./App.css";

const TOAST_OPTIONS = {
  style: { fontFamily: "'IBM Plex Sans Arabic', sans-serif" },
};

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F4EF]">
        <div className="text-[#5C605A] font-readex">جاري التحميل…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Force temp-password change before allowing the app
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

// Full app lock when the subscription has expired (admin exempt).
function RequireSubscription() {
  const { isAdmin } = useAuth();
  const { sub, loading } = useSubscription();
  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-[#5C605A] font-readex">جاري التحميل…</div>
      </div>
    );
  }
  if (!isAdmin && sub?.locked) return <Navigate to="/subscription" replace />;
  return <Outlet />;
}

function RedirectIfAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user && user.must_change_password) return <Navigate to="/change-password" replace />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <SubscriptionProvider>
        <BrowserRouter>
        <Toaster
          position="top-center"
          richColors
          dir="rtl"
          toastOptions={TOAST_OPTIONS}
        />
        <Routes>
          <Route path="/login" element={<RedirectIfAuth><LoginPage /></RedirectIfAuth>} />
          <Route path="/register" element={<RedirectIfAuth><RegisterPage /></RedirectIfAuth>} />
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route path="/terms" element={<TermsPage />} />

          <Route element={<RequireAuth><Layout /></RequireAuth>}>
            <Route path="/subscription" element={<SubscriptionPage />} />
            <Route element={<RequireSubscription />}>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/add" element={<AddInvoicePage />} />
              <Route path="/review" element={<ReviewInvoicePage />} />
              <Route path="/invoices" element={<InvoiceRegisterPage />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/reports/history" element={<MonthlyHistoryPage />} />
              <Route path="/settings" element={<SettingsPage />} />
            </Route>
            <Route path="/admin/users" element={<RequireAdmin><AdminUsersPage /></RequireAdmin>} />
            <Route path="/admin/coupons" element={<RequireAdmin><AdminCouponsPage /></RequireAdmin>} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </BrowserRouter>
      </SubscriptionProvider>
    </AuthProvider>
  );
}
